import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { all, delay, put, select, takeEvery } from 'typed-redux-saga';
import { ActionType, createReducer } from 'typesafe-actions';
import { clearError } from '@smart-builder/util';
import { ApiRequestRange } from '../../callApi';
import { createCancelAction } from './cancel';
import { createClearAction } from './clear';
import { createClearErrorAndPatchDataAction } from './clearErrorAndPatchData';
import { createDeleteAction } from './delete';
import { createDeleteNestedAction } from './deleteNested';
import { createInsertAction } from './insert';
import { createLoadAction } from './load';
import { createLoadMultiAction } from './loadMulti';
import { createRegisterAction } from './register';
import { createSaveAction } from './save';
import { createSelectRecordAction } from './selectRecord';
import { createSetBodyStateAction } from './setBodyState';
import {
  BodyState,
  EndPointBuilder,
  FieldChangeFormatter,
  FieldDefnOverrideFragment,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  ItemSummaryParser,
  KeyComparer,
  MainResourceActionCreators,
  MainResourceState,
  MainStateSelector,
  OnSuccessHandler,
  QueryParamSelector,
  RequestBodyFragment,
  ResourceListener,
} from './types';
import { createUnregisterAction } from './unregister';
import { createUpdateAction } from './update';
import { initialState } from './util';

const identityFormatter = (fieldName: any, value: any) => value;

// Main Resource are resources where we only load one body at a time (never in a grid)
//    and all changes are immediately sent to the server (rather than caching locally and sending onSave).
export function createMainResourceREST<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
  TItemSummaryAPI,
  TItemBodyAPI,
  TLoadMultiQueryParams,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  parseItemSummary: ItemSummaryParser<TItemSummary, TItemSummaryAPI>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
  formatFieldChange?: FieldChangeFormatter<TItemBody>,
  queryParamSelector?: QueryParamSelector<TItemBody>,
) {
  const { register, registerHandler } = createRegisterAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName);

  const { unregister, unregisterHandler } = createUnregisterAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName);

  const { deleteNested, deleteNestedHandler } = createDeleteNestedAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName, sameKey);

  const { selectRecord, selectRecordHandler } = createSelectRecordAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName, sameKey);

  const { clear, clearHandler } = createClearAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName);

  const { setBodyState, setBodyStateHandler } = createSetBodyStateAction<
    TItemKey,
    TItemSummary,
    TItemBody
  >(displayName);

  const {
    insertAsync,
    insertRequestSaga,
    insertRequestHandler,
    insertSuccessSaga,
    insertSuccessHandler,
    insertFailureSaga,
    insertFailureHandler,
  } = createInsertAction(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    parseItemBody,
    mergeItemBody,
    mergeSummaryBody,
  );

  const {
    loadAsync,
    loadRequestSaga,
    loadRequestHandler,
    loadSuccessHandler,
    loadFailureHandler,
  } = createLoadAction(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    parseItemBody,
    mergeItemBody,
    mergeSummaryBody,
    queryParamSelector ?? (() => undefined),
  );

  const {
    loadMultiAsync,
    loadMultiRequestSaga,
    loadMultiRequestHandler,
    loadMultiSuccessSaga,
    loadMultiSuccessHandler,
    loadMultiFailureHandler,
  } = createLoadMultiAction(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    parseItemSummary,
    loadAsync.request,
  );

  const {
    updateAsync,
    updateRequestSaga,
    updateRequestHandler,
    updateSuccessHandler,
    updateSuccessSaga,
    updateFailureHandler,
    updateFailureSaga,
  } = createUpdateAction(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    parseItemBody,
    mergeItemBody,
    mergeSummaryBody,
    formatFieldChange || identityFormatter,
    queryParamSelector ?? (() => undefined),
  );

  const {
    deleteAsync,
    deleteRequestSaga,
    deleteRequestHandler,
    deleteSuccessSaga,
    deleteSuccessHandler,
    deleteFailureSaga,
    deleteFailureHandler,
  } = createDeleteAction(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    queryParamSelector ?? (() => undefined),
  );

  const {
    cancelAsync,
    cancelRequestSaga,
    cancelRequestHandler,
    cancelSuccessHandler,
    cancelSuccessSaga,
    cancelFailureHandler,
    cancelFailureSaga,
  } = createCancelAction<TItemKey, TItemSummary, TItemBody, TItemBodyAPI>(
    displayName,
    getEndpoint,
    selector,
    sameKey,
    parseItemBody,
    mergeItemBody,
    mergeSummaryBody,
    queryParamSelector ?? (() => undefined),
  );

  const { clearErrorAndPatchData, clearErrorAndPatchDataHandler } =
    createClearErrorAndPatchDataAction(displayName);

  const { saveAsync, saveRequestSaga, saveFailureSaga, saveFailureHandler } =
    createSaveAction<TItemKey, TItemSummary, TItemBody>(
      displayName,
      selector,
      updateAsync.request,
    );

  // Tried using redux-saga's debounce - but couldn't get it to work.
  // Manually implementing the debounce here.
  const debounceTimeMS = 100;
  let registerCount = 0;

  function* registerSaga(params: ActionType<any>): Generator {
    if (params.payload.requireLoad === false) {
      return; // ==>
    }
    const reference = ++registerCount;
    yield delay(debounceTimeMS);

    if (reference === registerCount) {
      // this was the last register in this batch, perform a load
      const state = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;
      if (state.lastLoadParams) {
        yield put(loadAsync.request(state.lastLoadParams));
      }
    }
  }

  function* watches(): Generator {
    yield all([
      takeEvery(insertAsync.request, insertRequestSaga),
      takeEvery(insertAsync.success, insertSuccessSaga),
      takeEvery(insertAsync.failure, insertFailureSaga),
      takeEvery(loadAsync.request, loadRequestSaga),
      takeEvery(loadMultiAsync.request, loadMultiRequestSaga),
      takeEvery(loadMultiAsync.success, loadMultiSuccessSaga),
      takeEvery(updateAsync.request, updateRequestSaga),
      takeEvery(updateAsync.success, updateSuccessSaga),
      takeEvery(updateAsync.failure, updateFailureSaga),
      takeEvery(deleteAsync.request, deleteRequestSaga),
      takeEvery(deleteAsync.success, deleteSuccessSaga),
      takeEvery(deleteAsync.failure, deleteFailureSaga),
      takeEvery(cancelAsync.request, cancelRequestSaga),
      takeEvery(cancelAsync.success, cancelSuccessSaga),
      takeEvery(cancelAsync.failure, cancelFailureSaga),
      takeEvery(saveAsync.request, saveRequestSaga),
      takeEvery(saveAsync.failure, saveFailureSaga),
      takeEvery(register, registerSaga),
    ]);
  }

  const reducer = createReducer(initialState)
    .handleAction(register, registerHandler)
    .handleAction(unregister, unregisterHandler)
    .handleAction(deleteNested, deleteNestedHandler)
    .handleAction(selectRecord, selectRecordHandler)
    .handleAction(clear, clearHandler)
    .handleAction(clearErrorAndPatchData, clearErrorAndPatchDataHandler)
    .handleAction(setBodyState, setBodyStateHandler)
    .handleAction(insertAsync.request, insertRequestHandler)
    .handleAction(insertAsync.success, insertSuccessHandler)
    .handleAction(insertAsync.failure, insertFailureHandler)
    .handleAction(loadAsync.request, loadRequestHandler)
    .handleAction(loadAsync.success, loadSuccessHandler)
    .handleAction(loadAsync.failure, loadFailureHandler)
    .handleAction(loadMultiAsync.request, loadMultiRequestHandler)
    .handleAction(loadMultiAsync.success, loadMultiSuccessHandler)
    .handleAction(loadMultiAsync.failure, loadMultiFailureHandler)
    .handleAction(updateAsync.request, updateRequestHandler)
    .handleAction(updateAsync.success, updateSuccessHandler)
    .handleAction(updateAsync.failure, updateFailureHandler)
    .handleAction(cancelAsync.request, cancelRequestHandler)
    .handleAction(cancelAsync.success, cancelSuccessHandler)
    .handleAction(cancelAsync.failure, cancelFailureHandler)
    .handleAction(deleteAsync.request, deleteRequestHandler)
    .handleAction(deleteAsync.success, deleteSuccessHandler)
    .handleAction(deleteAsync.failure, deleteFailureHandler)
    .handleAction(saveAsync.failure, saveFailureHandler);

  const rawActions = {
    register,
    unregister,
    deleteNested,
    selectRecord,
    clear,
    clearErrorAndPatchData,
    setBodyState,
    insertAsync,
    loadAsync,
    loadMultiAsync,
    updateAsync,
    deleteAsync,
  };

  const actions: MainResourceActionCreators<
    TItemKey,
    TItemSummary,
    TItemBody,
    TLoadMultiQueryParams
  > = {
    register: (fragment, overrides, requireLoad) =>
      register({ fragment, overrides, requireLoad }),
    unregister: (fragment, overrides) => unregister({ fragment, overrides }),
    selectRecord: key => selectRecord({ key }),
    clear: () => clear({}),
    clearErrorAndPatchData: () => clearErrorAndPatchData({}),
    setBodyState: (bodyState: BodyState) => setBodyState({ bodyState }),

    load: (key, queryParams, bodyFragment) =>
      loadAsync.request({ key, queryParams, bodyFragment }),
    loadMulti: (queryParams?: TLoadMultiQueryParams, range?: ApiRequestRange) =>
      loadMultiAsync.request({ queryParams, range }),
    cancel: (onSuccess, onFailure) =>
      cancelAsync.request({ onSuccess, onFailure }),
    save: (onSuccess, onFailure, reloadSnapshots) =>
      saveAsync.request({ onSuccess, onFailure, reloadSnapshots }),
    insert: (body, data, onSuccess, onFailure, bodyFragment) =>
      insertAsync.request({ body, data, onSuccess, onFailure, bodyFragment }),
    update: (fieldName, value, op = 'update', onSuccess, onFailure) =>
      updateAsync.request({ fieldName, value, op, onSuccess, onFailure }),
    delete: (key, onSuccess, onFailure) =>
      deleteAsync.request({ key, onSuccess, onFailure }),
    deleteNested: key => deleteNested({ key }),
  };

  const useListener: ResourceListener = (
    requestBodyFragment,
    overrides, // Special value.
    deps,
    doListen = true,
  ) => {
    const dispatch = useDispatch();
    useEffect(() => {
      if (requestBodyFragment && doListen) {
        dispatch(actions.register(requestBodyFragment, overrides));
        return () => {
          dispatch(actions.unregister(requestBodyFragment, overrides));
        };
      }
    }, deps);
  };

  const useWizard = (
    requestWizardPath: string,
    additionalBody?: any,
    wizardInitValue?: any,
    onInitSuccess?: OnSuccessHandler<TItemBody>,
    onInitFailure?: () => void,
  ) => {
    const dispatch = useDispatch();
    const { currentBody, lastLoadParams, cancelCount } = useSelector(selector);
    const bodyMatchesLoadParams =
      currentBody && lastLoadParams && sameKey(currentBody, lastLoadParams.key);

    const [initStateCount, setInitStateCount] = useState(0);

    const reInitWizard = useCallback(
      () => setInitStateCount(prev => prev + 1),
      [],
    );

    // T12080 SS 20230518 Can't depend on the wizardInitValue directly as it will most likely be an object
    // which are only checked for referential equality. Stringify-ing first so that the useEffect only fires if the
    // contents change.
    const initValueJSON = JSON.stringify(wizardInitValue);
    useEffect(() => {
      const overrides: any[] = [];

      if (bodyMatchesLoadParams && requestWizardPath) {
        const requestBodyFragment = {
          [requestWizardPath]: { fields: ['*'] },
          ...additionalBody,
        };
        dispatch(actions.register(requestBodyFragment, overrides, false));
        dispatch(
          actions.update(
            requestWizardPath,
            wizardInitValue ?? '',
            'initialise',
            onInitSuccess,
            onInitFailure,
          ),
        );
        return () => {
          dispatch(actions.unregister(requestBodyFragment, overrides));
        };
      }
    }, [
      requestWizardPath,
      bodyMatchesLoadParams,
      initStateCount,
      cancelCount,
      initValueJSON,
      // We don't want the above to re-run just because the onInit functions changed.
      // onInitSuccess,
      // onInitFailure,
    ]);
    return reInitWizard;
  };

  return {
    rawActions,
    actions,
    watches,
    reducer,
    useListener,
    useWizard,
  };
}
